import { FontPoppins } from '@/constants/font';
import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TextInputProps,
  View,
} from 'react-native';
import MaskInput, { Masks } from 'react-native-mask-input';
import { brazilPhoneMask } from '@/utils/brazil-phone';
import { ThemedText } from '../themed-text';
import { createThemedStyles, useThemedHex } from '@/hooks/use-theme-colors';

// Helper para processar valores de moeda
const handleCurrencyChange = (
  text: string,
  currentValue: string | null | undefined,
  onChange: (value: string) => void,
) => {
  // Extrai apenas os dígitos do texto digitado (sem formatação)
  const digits = text.replace(/\D/g, '');

  // Se não há dígitos, define como vazio
  if (digits.length === 0) {
    onChange('');
    return;
  }

  // Sempre trata os dígitos como centavos (divide por 100)
  // Isso evita o problema de concatenar "0,00" com novos dígitos
  // Exemplo: digitar "1" resulta em "R$ 0,01", não "R$ 0,001"
  const valueInReais = parseInt(digits, 10) / 100;
  const formatted = valueInReais.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  onChange(formatted);
};

type MaskType = 'phone' | 'cpf' | 'cnpj' | 'cpfCnpj' | 'cep' | 'date' | 'currency' | undefined;

type ControlledInputProps = {
  control: Control<any>;
  name: string;
  label?: string;
  /** Texto de apoio exibido abaixo do campo, em linguagem simples */
  helperText?: string;
  type?: 'text' | 'password' | 'email' | 'number' | 'textarea';
  maskType?: MaskType;
  icon?: React.ComponentProps<typeof Ionicons>['name'];
  disabled?: boolean;
  /** Padrão de posicionamento (para consistência com selects): 'center'. Não afeta input de texto. */
  dropdownPlacement?: 'top' | 'center';
  /** Sem a margem inferior própria: quem empilha os campos controla o respiro. */
  compact?: boolean;
} & TextInputProps;

const BORDER_DEFAULT = '#E5E5E5';
const BORDER_FOCUS = '#FD5001';
const BORDER_ERROR = '#EF4444';

export function ControlledInput({
  control,
  name,
  label,
  helperText,
  type = 'text',
  secureTextEntry,
  maskType,
  icon,
  disabled = false,
  dropdownPlacement = 'center',
  multiline,
  compact,
  ...textInputProps
}: ControlledInputProps) {
  const th = useThemedHex();
  const styles = useStyles();
  const isSecurityField = !!secureTextEntry;
  const isMaskedInput = !!maskType;
  const isTextarea = type === 'textarea' || !!multiline;

  const [isPasswordVisible, setPasswordVisible] = useState(isSecurityField);
  const [isFocused, setIsFocused] = useState(false);

  const togglePasswordVisibility = () => {
    setPasswordVisible(!isPasswordVisible);
  };

  const mask = (() => {
    switch (maskType) {
      case 'phone':
        return brazilPhoneMask;
      case 'cpf':
        return Masks.BRL_CPF;
      case 'cnpj':
        return Masks.BRL_CNPJ;
      case 'cpfCnpj':
        return (text?: string) => {
          const digits = (text || '').replace(/\D/g, '');
          return digits.length > 11 ? Masks.BRL_CNPJ : Masks.BRL_CPF;
        };
      case 'cep':
        return [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/];
      case 'date':
        return Masks.DATE_DDMMYYYY;
      case 'currency':
        // Para currency, não usamos máscara - formatamos manualmente
        return undefined;
      default:
        return undefined;
    }
  })();

  // Para currency, usamos TextInput ao invés de MaskInput pois formatamos manualmente
  const InputComponent = maskType === 'currency' ? TextInput : mask ? MaskInput : TextInput;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
        <View style={[styles.container, compact && styles.containerCompact]}>
          {label && (
            <ThemedText type="labelInput" style={styles.label} className="text-neutral-700">
              {label}
            </ThemedText>
          )}
          <View className="relative">
            {icon && (
              <View style={styles.leftIcon}>
                <Ionicons className="text-neutral-400" name={icon} size={18} />
              </View>
            )}
            <InputComponent
              style={[
                styles.input,
                isTextarea && styles.textarea,
                {
                  paddingLeft: icon ? 42 : 16,
                  borderColor: error ? BORDER_ERROR : isFocused ? BORDER_FOCUS : th(BORDER_DEFAULT),
                },
                disabled && styles.inputDisabled,
                Platform.OS === 'web' && ({ outline: 'none' } as object),
              ]}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                setIsFocused(false);
                onBlur();
              }}
              onChangeText={
                maskType === 'currency'
                  ? (text: string) => handleCurrencyChange(text, value, onChange)
                  : onChange
              }
              value={value === null || value === undefined ? '' : String(value)}
              secureTextEntry={isSecurityField ? isPasswordVisible : false}
              keyboardType={
                type === 'number' || maskType === 'currency'
                  ? 'numeric'
                  : type === 'email'
                    ? 'email-address'
                    : 'default'
              }
              multiline={isTextarea}
              numberOfLines={isTextarea ? 4 : 1}
              editable={!disabled}
              placeholderTextColor="#A3A3A3"
              {...(isMaskedInput && maskType !== 'currency' && { mask })}
              {...textInputProps}
            />
            {secureTextEntry && (
              <Pressable className="absolute right-4 top-3.5" onPress={togglePasswordVisibility}>
                <Ionicons
                  className="text-neutral-400"
                  name={isPasswordVisible ? 'eye-off' : 'eye'}
                  size={20}
                />
              </Pressable>
            )}
          </View>

          {error ? (
            <Text style={styles.errorText}>{error.message}</Text>
          ) : helperText ? (
            <Text style={styles.helperText}>{helperText}</Text>
          ) : null}
        </View>
      )}
    />
  );
}

const useStyles = createThemedStyles((th) =>
  StyleSheet.create({
    container: {
      marginBottom: 16,
      width: '100%',
      position: 'relative',
    },
    containerCompact: {
      marginBottom: 0,
    },
    label: {
      marginBottom: 6,
    },
    leftIcon: {
      position: 'absolute',
      left: 14,
      top: 15,
      zIndex: 1,
    },
    input: {
      minHeight: 48,
      borderWidth: 1,
      borderRadius: 12,
      borderCurve: 'continuous',
      backgroundColor: th('#FFFFFF'),
      paddingHorizontal: 16,
      paddingVertical: 12,
      fontSize: 16,
      color: th('#171717'),
      fontFamily: FontPoppins.REGULAR,
    },
    inputDisabled: {
      backgroundColor: th('#FAFAFA'),
      color: th('#737373'),
    },
    textarea: {
      height: 110,
      textAlignVertical: 'top',
    },
    errorText: {
      color: BORDER_ERROR,
      fontFamily: FontPoppins.REGULAR,
      fontSize: 12,
      marginTop: 4,
      marginLeft: 4,
    },
    helperText: {
      color: th('#737373'),
      fontFamily: FontPoppins.REGULAR,
      fontSize: 12,
      marginTop: 4,
      marginLeft: 4,
    },
  }),
);
